import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContextSupabase';
import { User, Settings, Copy, Check, Plus, Trash2, Users, Building2, Shield, X, AlertTriangle, AlertCircle, CreditCard, Lock, Coins, ArrowRight } from 'lucide-react';
import { joinAgency, deleteAccount, supabase, deleteUserFromSupabase } from '../lib/supabase';
import { Dialog } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';

interface Agency {
  id: string;
  name: string;
  createdAt: { seconds: number };
  updatedAt: { seconds: number };
}

interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  userRole: 'admin' | 'user';
  permissions?: {
    collaborations: boolean;
    todo: boolean;
    clients: boolean;
    influencers: boolean;
    casting: boolean;
    accounting: boolean;
    resources: boolean;
    prospection: boolean;
    assistant: boolean;
    talent_manager: boolean;
    scoot: boolean;
    matching_ia: boolean;
  };
  createdAt: Date;
}

function CreditsDisplay() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [creditsBalance, setCreditsBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (userProfile?.agency_id) {
      loadCredits();
    }
  }, [userProfile?.agency_id]);

  const loadCredits = async () => {
    if (!userProfile?.agency_id) return;

    try {
      const { data, error } = await supabase.rpc('get_organization_credits', {
        org_id: userProfile.agency_id,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setCreditsBalance(data[0].balance);
      }
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.04]">
        <div className="p-6">
          <div className="animate-pulse flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-lg"></div>
            <div className="flex-1">
              <div className="h-4 bg-white/10 rounded w-1/3 mb-2"></div>
              <div className="h-6 bg-white/10 rounded w-1/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.06]">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-500/20 rounded-lg">
              <Coins className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white mb-2">
                Crédits de l'organisation
              </h2>
              <div className="space-y-1 text-sm text-white/50">
                <p className="text-2xl font-bold text-white">
                  {creditsBalance} crédits
                </p>
                <p className="mt-2">
                  Utilisés pour les fonctionnalités IA et enrichissement de données
                </p>
                <div className="mt-4 space-y-1 text-xs text-white/40">
                  <p>• Matching IA : 2 crédits</p>
                  <p>• Email des leads : 1 crédit</p>
                  <p>• Refresh profil : 1 crédit</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/app/credits')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-black bg-white rounded-xl hover:bg-white/90"
            >
              Acheter des crédits
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Account() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [isProfileEditing, setIsProfileEditing] = useState(false);
  const [isAgencyEditing, setIsAgencyEditing] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [agencyName, setAgencyName] = useState('');
  const [agency, setAgency] = useState<Agency | null>(null);
  const [agencyCode, setAgencyCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: '',
  });
  const [permissionsData, setPermissionsData] = useState<Record<string, boolean>>({});
  const [isLoadingData, setIsLoadingData] = useState(false);

  useEffect(() => {
    console.log('Account page - userProfile:', userProfile);
    console.log('Account page - agency_id:', userProfile?.agency_id);

    const fetchAgencyDetails = async () => {
      if (userProfile?.agency_id) {
        try {
          setIsLoadingData(true);

          const { data: agencyData, error: agencyError } = await supabase
            .from('agencies')
            .select('*')
            .eq('id', userProfile.agency_id)
            .maybeSingle();

          if (agencyError) throw agencyError;

          if (agencyData) {
            setAgency(agencyData as Agency);
            setAgencyName(agencyData.name);
          }

          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const response = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-team-members`,
              {
                headers: {
                  'Authorization': `Bearer ${session.access_token}`,
                  'Content-Type': 'application/json',
                },
              }
            );

            if (!response.ok) throw new Error('Failed to fetch team members');

            const { teamMembers: teamData } = await response.json();

            if (teamData) {
              const teamMembers = teamData.map((u: any) => ({
                id: u.id,
                email: u.email,
                firstName: u.first_name,
                lastName: u.last_name,
                role: u.role,
                isAdmin: u.user_role === 'admin',
                permissions: u.permissions,
                createdAt: new Date(u.created_at)
              })) as TeamMember[];
              setTeamMembers(teamMembers);
            }
          }
        } catch (error) {
          console.error('Error fetching agency details:', error);
          setError('Une erreur est survenue lors de la récupération des détails de l\'agence.');
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchAgencyDetails();

    if (userProfile?.agency_id) {
      const channel = supabase
        .channel('users-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'users',
            filter: `agency_id=eq.${userProfile.agency_id}`
          },
          () => {
            fetchAgencyDetails();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [userProfile?.agency_id]);

  const loadData = async () => {
    if (userProfile?.agency_id) {
      try {
        setIsLoadingData(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-team-members`,
            {
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (!response.ok) throw new Error('Failed to fetch team members');

          const { teamMembers: teamData } = await response.json();

          if (teamData) {
            const teamMembers = teamData.map((u: any) => ({
              id: u.id,
              email: u.email,
              firstName: u.first_name,
              lastName: u.last_name,
              role: u.role,
              isAdmin: u.user_role === 'admin',
              permissions: u.permissions,
              createdAt: new Date(u.created_at)
            })) as TeamMember[];
            setTeamMembers(teamMembers);
          }
        }
      } catch (error) {
        console.error('Error loading team data:', error);
      } finally {
        setIsLoadingData(false);
      }
    }
  };

  useEffect(() => {
    if (userProfile) {
      setFormData({
        firstName: userProfile.first_name || '',
        lastName: userProfile.last_name || '',
        role: userProfile.role || '',
      });
    }
  }, [userProfile]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (!currentUser?.id) return;

      const { error: updateError } = await supabase
        .from('users')
        .update({
          first_name: formData.firstName,
          last_name: formData.lastName,
          role: formData.role,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentUser.id);

      if (updateError) throw updateError;

      setSuccess('Vos informations ont été mises à jour avec succès.');
      setIsProfileEditing(false);
      
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Une erreur est survenue lors de la mise à jour.');
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await deleteAccount(deletePassword);
      setIsDeleteModalOpen(false);
      navigate('/login');
    } catch (error: any) {
      setError(error.message || 'Une erreur est survenue lors de la suppression du compte.');
    }
  };

  const handleUpdateAgency = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (!userProfile?.agency_id) return;

      const { error: updateError } = await supabase
        .from('agencies')
        .update({
          name: agencyName,
          updated_at: new Date().toISOString()
        })
        .eq('id', userProfile.agency_id);

      if (updateError) throw updateError;

      setAgency(prev => prev ? { ...prev, name: agencyName } : null);
      setSuccess('Les informations ont été mises à jour avec succès.');
      setIsAgencyEditing(false);
    } catch (error) {
      setError('Une erreur est survenue lors de la mise à jour.');
    }
  };

  const handleJoinAgency = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await joinAgency(agencyCode);
      setSuccess('Vous avez rejoint l\'agence avec succès.');
      setIsJoining(false);
      window.location.reload();
    } catch (error: any) {
      setError(error.message || 'Une erreur est survenue lors de la tentative de rejoindre l\'agence.');
    }
  };

  const copyAgencyId = async () => {
    if (agency?.id) {
      await navigator.clipboard.writeText(agency.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedMember || !selectedMember.id) {
      setError('Aucun utilisateur sélectionné ou ID manquant');
      return;
    }
    
    try {
      await deleteUserFromSupabase(selectedMember.id);
      
      // Mettre à jour la liste des membres
      await loadData();
      
      // Fermer le modal et réinitialiser l'utilisateur sélectionné
      setIsDeleteUserModalOpen(false);
      setSelectedMember(null);
      
      // Afficher un message de succès
      setSuccessMessage(`Le compte de ${selectedMember.firstName} ${selectedMember.lastName} a été supprimé avec succès.`);
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Une erreur est survenue lors de la suppression du compte.');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (updateError) throw updateError;

      setSuccessMessage('Votre mot de passe a été changé avec succès.');
      setIsPasswordModalOpen(false);
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      console.error('Error updating password:', error);
      setError(error.message || 'Une erreur est survenue lors du changement de mot de passe.');
    }
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return '';
    if (typeof date === 'object' && 'seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const inputClasses = 'mt-1 block w-full rounded-xl bg-white/[0.06] border border-white/[0.08] text-white px-3 py-2 focus:ring-1 focus:ring-white/20 outline-none transition-all';

  return (
    <div className="space-y-6">
      {successMessage && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-3 rounded-xl">
          {successMessage}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src="https://i.postimg.cc/jq4qbh4Y/logo-campaiyn.png"
            alt="Campaly"
            className="w-12 h-12 object-contain brightness-0 invert"
          />
          <div>
            <h1 className="text-3xl font-bold text-white">Compte</h1>
            <p className="text-white/40">Gérez vos paramètres personnels</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl hover:bg-red-500/20 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Supprimer mon compte
          </button>
        </div>
      </div>

      <CreditsDisplay />

      <div className="rounded-xl border border-white/[0.08] bg-white/[0.04]">
        <div className="p-6 space-y-6">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Informations personnelles</h2>
              <button
                onClick={() => setIsProfileEditing(true)}
                className="text-sm text-white/50 hover:text-white transition-colors"
              >
                Modifier
              </button>
            </div>

            {isProfileEditing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-3 rounded-xl">
                    {success}
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1">
                      Prénom
                    </label>
                    <input type="text" id="firstName" value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className={inputClasses} />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1">
                      Nom
                    </label>
                    <input type="text" id="lastName" value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className={inputClasses} />
                  </div>
                </div>

                <div>
                  <label htmlFor="role" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1">
                    Rôle dans l'entreprise
                  </label>
                  <input type="text" id="role" value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className={inputClasses} />
                </div>

                <div className="flex justify-end space-x-3">
                  <button type="button" onClick={() => setIsProfileEditing(false)}
                    className="px-4 py-2 text-sm font-medium text-white/60 bg-white/[0.06] border border-white/[0.08] rounded-xl hover:bg-white/[0.10]">
                    Annuler
                  </button>
                  <button type="submit"
                    className="px-4 py-2 text-sm font-semibold text-black bg-white rounded-xl hover:bg-white/90">
                    Sauvegarder
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-white/[0.04] p-4 rounded-xl border border-white/[0.06]">
                  <div className="flex items-center space-x-4">
                    <div className="bg-white/10 p-3 rounded-full">
                      <User className="w-6 h-6 text-white/60" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-white">
                        {userProfile?.firstName} {userProfile?.lastName}
                      </p>
                      <p className="text-sm text-white/40">{currentUser?.email}</p>
                      <p className="text-sm text-white/40">{userProfile?.role || 'Rôle non défini'}</p>
                      <p className="text-sm text-white/40">
                        Type de compte: {userProfile?.user_role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setIsPasswordModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white/60 bg-white/[0.06] rounded-xl hover:bg-white/[0.10] border border-white/[0.08] transition-colors"
                >
                  <Lock className="w-4 h-4" />
                  Changer mon mot de passe
                </button>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Informations de l'agence</h2>
              {!userProfile?.agency_id && (
                <button
                  onClick={() => setIsJoining(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-black bg-white rounded-xl hover:bg-white/90"
                >
                  <Plus className="w-4 h-4" />
                  Rejoindre une agence
                </button>
              )}
            </div>

            {isJoining ? (
              <form onSubmit={handleJoinAgency} className="space-y-4">
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl">{error}</div>
                )}
                {success && (
                  <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-3 rounded-xl">{success}</div>
                )}
                <div>
                  <label htmlFor="agencyCode" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1">
                    Code de l'agence
                  </label>
                  <input type="text" id="agencyCode" value={agencyCode}
                    onChange={(e) => setAgencyCode(e.target.value)}
                    className={inputClasses} required />
                </div>
                <div className="flex justify-end space-x-3">
                  <button type="button" onClick={() => setIsJoining(false)}
                    className="px-4 py-2 text-sm text-white/60 bg-white/[0.06] border border-white/[0.08] rounded-xl hover:bg-white/[0.10]">
                    Annuler
                  </button>
                  <button type="submit"
                    className="px-4 py-2 text-sm font-semibold text-black bg-white rounded-xl hover:bg-white/90">
                    Rejoindre
                  </button>
                </div>
              </form>
            ) : (
              <div className="bg-white/[0.04] p-4 rounded-xl border border-white/[0.06] space-y-4">
                {userProfile?.agency_id ? (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-white/50" />
                        <div>
                          <p className="text-sm font-medium text-white">Nom de l'agence</p>
                          <p className="text-sm text-white/40">{agency?.name || 'Non défini'}</p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <p className="text-sm font-medium text-white">Date de création</p>
                        <p className="text-sm text-white/40">
                          {agency?.createdAt ? formatDate(agency.createdAt) : 'Non défini'}
                        </p>
                      </div>
                    </div>
                    {userProfile?.user_role === 'admin' && (
                      <button onClick={() => setIsAgencyEditing(true)}
                        className="text-sm text-white/50 hover:text-white transition-colors">
                        Modifier
                      </button>
                    )}

                    {isAgencyEditing ? (
                      <form onSubmit={handleUpdateAgency} className="space-y-4 pt-4 border-t border-white/[0.06]">
                        <div>
                          <label htmlFor="agencyName" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1">
                            Nom de l'agence
                          </label>
                          <input type="text" id="agencyName" value={agencyName}
                            onChange={(e) => setAgencyName(e.target.value)}
                            className={inputClasses} required />
                        </div>
                        <div className="flex justify-end space-x-3">
                          <button type="button" onClick={() => setIsAgencyEditing(false)}
                            className="px-4 py-2 text-sm text-white/60 bg-white/[0.06] border border-white/[0.08] rounded-xl hover:bg-white/[0.10]">
                            Annuler
                          </button>
                          <button type="submit"
                            className="px-4 py-2 text-sm font-semibold text-black bg-white rounded-xl hover:bg-white/90">
                            Sauvegarder
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="pt-4 border-t border-white/[0.06]">
                        <p className="text-sm font-medium text-white mb-2">Code de l'agence</p>
                        <div className="flex items-center justify-between bg-white/[0.06] p-3 rounded-xl border border-white/[0.08]">
                          <code className="text-sm text-white/40">{agency?.id}</code>
                          <button onClick={copyAgencyId}
                            className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            {copied ? 'Copié !' : 'Copier'}
                          </button>
                        </div>
                        <p className="mt-2 text-sm text-white/30">
                          Partagez ce code avec les membres de votre équipe pour qu'ils puissent rejoindre votre agence.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-white/40">Vous n'êtes pas encore membre d'une agence.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {userProfile?.agency_id && teamMembers.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Membres de l'équipe</h2>
                <div className="bg-white/[0.06] px-3 py-1 rounded-lg text-sm text-white/40 border border-white/[0.08]">
                  {teamMembers.length} membre{teamMembers.length > 1 ? 's' : ''}
                </div>
              </div>
              <div className="rounded-xl divide-y divide-white/[0.06] border border-white/[0.08] bg-white/[0.02]">
                {teamMembers.map((member) => (
                  <div
                    key={member.id}
                    className={`p-4 ${userProfile?.user_role === 'admin' ? 'cursor-pointer hover:bg-white/[0.04]' : ''} transition-colors`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (userProfile?.user_role === 'admin' && member.id !== currentUser?.id) {
                        setSelectedMember(member);
                        setPermissionsData({
                          dashboard: false, collaborations: false, casting: false, todo: false,
                          daily_brief: false, clients: false, influencers: false, discover: false,
                          recruitment: false, accounting: false, resources: false, prospection: false,
                          scoot: false, matching_ia: false, admin: false,
                          ...(member.permissions || {})
                        });
                        setIsPermissionsModalOpen(true);
                      }
                    }}
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="w-5 h-5 text-white/40" />
                          <div>
                            <span className="text-white font-medium">{member.firstName} {member.lastName}</span>
                            <p className="text-sm text-white/40">{member.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-white/40">{member.role || 'Rôle non défini'}</p>
                          <span className={`inline-flex items-center px-2 py-1 mt-1 rounded-full text-xs font-medium ${
                            member.userRole === 'admin'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-white/10 text-white/40'
                          }`}>
                            {member.userRole === 'admin' ? 'Administrateur' : 'Utilisateur'}
                          </span>
                        </div>
                      </div>

                      {userProfile?.user_role === 'admin' && member.id !== currentUser?.id && (
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.06]">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedMember(member);
                              setPermissionsData({
                                dashboard: false, collaborations: false, casting: false, todo: false,
                                daily_brief: false, clients: false, influencers: false, discover: false,
                                recruitment: false, accounting: false, resources: false, prospection: false,
                                scoot: false, matching_ia: false, assistant: false, admin: false,
                                ...(member.permissions || {})
                              });
                              setIsPermissionsModalOpen(true);
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 bg-white/[0.08] text-white/60 rounded-lg hover:bg-white/[0.14] transition-colors"
                          >
                            <Shield className="w-4 h-4" />
                            <span className="text-sm">Gérer les permissions</span>
                          </button>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedMember(member);
                              setIsDeleteUserModalOpen(true);
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span className="text-sm">Supprimer</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-white mb-1">Supprimer le compte</h3>
            <p className="text-sm text-white/40 mb-4">Cette action est irréversible. Toutes vos données seront définitivement supprimées.</p>
            <form onSubmit={handleDeleteAccount} className="space-y-4">
              {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl">{error}</div>}
              <div>
                <label htmlFor="password" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1">Confirmez votre mot de passe</label>
                <input type="password" id="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)}
                  className={inputClasses} required />
              </div>
              <div className="flex justify-end space-x-3">
                <button type="button" onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 text-sm text-white/60 bg-white/[0.06] border border-white/[0.08] rounded-xl hover:bg-white/[0.10]">
                  Annuler
                </button>
                <button type="submit"
                  className="px-4 py-2 text-sm font-semibold text-white bg-red-600/80 rounded-xl hover:bg-red-600">
                  Supprimer définitivement
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      <Dialog open={isPermissionsModalOpen} onClose={() => setIsPermissionsModalOpen(false)} className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-semibold text-white">Permissions utilisateur</h3>
              <button onClick={() => setIsPermissionsModalOpen(false)} className="text-white/30 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            {selectedMember && (
              <p className="text-sm text-white/40 mb-4">
                Gérer les permissions pour {selectedMember.firstName} {selectedMember.lastName}
              </p>
            )}

            <div className="space-y-4 max-h-96 overflow-y-auto">
              <div className="p-4 bg-white/[0.04] rounded-xl border border-white/[0.06]">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-white/50" />
                    <span className="font-medium text-white">Administrateur</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" checked={permissionsData.admin || false}
                      onChange={(e) => setPermissionsData(prev => ({ ...prev, admin: e.target.checked }))} />
                    <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-white/30 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>
                <p className="text-sm text-white/30">Donne accès à toutes les fonctionnalités et à la gestion des utilisateurs</p>
              </div>

              <div className="p-4 bg-white/[0.04] rounded-xl border border-white/[0.06]">
                <h4 className="font-medium text-white mb-3">Modules</h4>
                <p className="text-xs text-white/30 mb-3 pb-3 border-b border-white/[0.06]">
                  La section Découverte (Discover, Market Watch, Recrutement) est accessible à tous par défaut.
                </p>
                <div className="space-y-3">
                  {[
                    { key: 'dashboard', label: 'Dashboard', description: 'Accès au tableau de bord' },
                    { key: 'collaborations', label: 'Collaborations', description: 'Gestion des campagnes' },
                    { key: 'casting', label: 'Casting', description: 'Gestion des castings' },
                    { key: 'todo', label: 'Mes tâches', description: 'Gestion des tâches' },
                    { key: 'daily_brief', label: 'Tâches influenceurs', description: 'Suivi des tâches influenceurs' },
                    { key: 'clients', label: 'Clients', description: 'Gestion des clients' },
                    { key: 'influencers', label: 'Influenceurs', description: 'Gestion des influenceurs' },
                    { key: 'accounting', label: 'Comptabilité', description: 'Gestion de la comptabilité' },
                    { key: 'resources', label: 'Ressources', description: 'Accès aux ressources' },
                    { key: 'prospection', label: 'Prospection', description: 'Gestion des leads' },
                    { key: 'scoot', label: 'Scoot', description: 'Recherche avec IA' },
                    { key: 'matching_ia', label: 'Matching IA', description: 'Matching intelligent' },
                    { key: 'talent_manager', label: 'Talent Manager', description: 'Gestion des créateurs' },
                    { key: 'casting_interne', label: 'Casting interne', description: 'Casting interne' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between">
                      <div>
                        <p className="text-white/80 text-sm">{item.label}</p>
                        <p className="text-xs text-white/30">{item.description}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer"
                          checked={permissionsData[item.key as keyof typeof permissionsData] || false}
                          onChange={(e) => setPermissionsData(prev => ({ ...prev, [item.key]: e.target.checked }))} />
                        <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-white/30 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setIsPermissionsModalOpen(false)}
                className="px-4 py-2 text-sm text-white/60 bg-white/[0.06] border border-white/[0.08] rounded-xl hover:bg-white/[0.10]">
                Annuler
              </button>
              <button
                onClick={async () => {
                  if (selectedMember) {
                    try {
                      const updateData: any = { permissions: permissionsData, updated_at: new Date().toISOString() };
                      if (permissionsData.admin) updateData.user_role = 'admin';
                      else if (selectedMember.isAdmin) updateData.user_role = 'user';
                      const { error: updateError } = await supabase.from('users').update(updateData).eq('id', selectedMember.id).select();
                      if (updateError) throw updateError;
                      await loadData();
                      setIsPermissionsModalOpen(false);
                      setSuccessMessage(`Les permissions de ${selectedMember.firstName} ${selectedMember.lastName} ont été mises à jour.`);
                      setTimeout(() => setSuccessMessage(null), 3000);
                    } catch (error) {
                      setError('Une erreur est survenue lors de la mise à jour des permissions.');
                    }
                  }
                }}
                className="px-4 py-2 text-sm font-semibold text-black bg-white rounded-xl hover:bg-white/90">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog open={isDeleteUserModalOpen} onClose={() => setIsDeleteUserModalOpen(false)} className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Supprimer l'utilisateur</h3>
                <p className="text-sm text-white/40">Cette action est irréversible</p>
              </div>
            </div>
            {selectedMember && (
              <div className="p-4 bg-white/[0.04] rounded-xl border border-white/[0.06] mb-4">
                <p className="text-white font-medium">{selectedMember.firstName} {selectedMember.lastName}</p>
                <p className="text-sm text-white/40">{selectedMember.email}</p>
                <p className="text-sm text-white/30 mt-1">{selectedMember.role || 'Rôle non défini'}</p>
              </div>
            )}
            <p className="text-white/50 text-sm mb-6">
              Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action supprimera définitivement son accès à votre agence.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setIsDeleteUserModalOpen(false)}
                className="px-4 py-2 text-sm text-white/60 bg-white/[0.06] border border-white/[0.08] rounded-xl hover:bg-white/[0.10]">
                Annuler
              </button>
              <button onClick={handleDeleteUser}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600/80 rounded-xl hover:bg-red-600">
                Supprimer l'utilisateur
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog open={isPasswordModalOpen} onClose={() => { setIsPasswordModalOpen(false); setPasswordData({ newPassword: '', confirmPassword: '' }); setError(''); }} className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-semibold text-white">Changer mon mot de passe</h3>
              <button onClick={() => { setIsPasswordModalOpen(false); setPasswordData({ newPassword: '', confirmPassword: '' }); setError(''); }}
                className="text-white/30 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-white/40 mb-4">Votre nouveau mot de passe doit contenir au moins 6 caractères.</p>
            <form onSubmit={handleChangePassword} className="space-y-4">
              {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl">{error}</div>}
              <div>
                <label htmlFor="newPassword" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1">Nouveau mot de passe</label>
                <input type="password" id="newPassword" value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className={inputClasses} required minLength={6} />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1">Confirmer le mot de passe</label>
                <input type="password" id="confirmPassword" value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className={inputClasses} required minLength={6} />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => { setIsPasswordModalOpen(false); setPasswordData({ newPassword: '', confirmPassword: '' }); setError(''); }}
                  className="px-4 py-2 text-sm text-white/60 bg-white/[0.06] border border-white/[0.08] rounded-xl hover:bg-white/[0.10]">
                  Annuler
                </button>
                <button type="submit" className="px-4 py-2 text-sm font-semibold text-black bg-white rounded-xl hover:bg-white/90">
                  Changer le mot de passe
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </div>
  );
}